// Org-chart interaction hooks: element size tracking and hand-rolled pan/zoom.
// All pan/zoom callbacks read refs + use functional setT so they stay stable
// and the native wheel listener only ever attaches once.

import { useCallback, useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { clamp, PAN_MARGIN, ZOOM_MAX, ZOOM_MIN } from './orgChartLayout';

export interface Transform {
  s: number;
  x: number;
  y: number;
}

export interface ElementSize {
  w: number;
  h: number;
}

export const useElementSize = (): [React.RefObject<HTMLDivElement>, ElementSize] => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<ElementSize>({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  return [ref, size];
};

export interface PanZoom {
  t: Transform;
  setT: Dispatch<SetStateAction<Transform>>;
  setBounds: (contentW: number, contentH: number, viewW: number, viewH: number) => void;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  endDrag: () => void;
  onWheel: (e: WheelEvent, el: HTMLElement) => void;
  zoomBy: (factor: number, viewW: number, viewH: number) => void;
  fit: (viewW: number, viewH: number, boundsW: number, boundsH: number, pad?: number) => void;
}

export const usePanZoom = (min = ZOOM_MIN, max = ZOOM_MAX): PanZoom => {
  const [t, setT] = useState<Transform>({ s: 1, x: 40, y: 40 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // Keep at least part of the content on screen so panning/zooming can never
  // strand the user on an empty canvas.
  const bounds = useRef<{ cw: number; ch: number; vw: number; vh: number } | null>(null);

  const setBounds = useCallback((cw: number, ch: number, vw: number, vh: number) => {
    bounds.current = { cw, ch, vw, vh };
  }, []);

  const clampPan = useCallback((s: number, x: number, y: number) => {
    const b = bounds.current;
    if (!b || !b.vw || !b.vh) return { x, y };
    const sw = b.cw * s;
    const sh = b.ch * s;
    const loX = b.vw - PAN_MARGIN - sw;
    const hiX = PAN_MARGIN;
    const loY = b.vh - PAN_MARGIN - sh;
    const hiY = PAN_MARGIN;
    return {
      x: clamp(x, Math.min(loX, hiX), Math.max(loX, hiX)),
      y: clamp(y, Math.min(loY, hiY), Math.max(loY, hiY)),
    };
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      drag.current = { x: e.clientX, y: e.clientY, ox: t.x, oy: t.y };
    },
    [t.x, t.y],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!drag.current) return;
      const nx = drag.current.ox + (e.clientX - drag.current.x);
      const ny = drag.current.oy + (e.clientY - drag.current.y);
      setT((p) => ({ ...p, ...clampPan(p.s, nx, ny) }));
    },
    [clampPan],
  );

  const endDrag = useCallback(() => {
    drag.current = null;
  }, []);

  const onWheel = useCallback(
    (e: WheelEvent, el: HTMLElement) => {
      e.preventDefault();
      // Read geometry from the element NOW — never inside the setT updater,
      // which React may run after the native event has been recycled.
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0012);
      setT((p) => {
        const ns = clamp(p.s * factor, min, max);
        const k = ns / p.s; // zoom toward cursor
        return { s: ns, ...clampPan(ns, mx - (mx - p.x) * k, my - (my - p.y) * k) };
      });
    },
    [min, max, clampPan],
  );

  const zoomBy = useCallback(
    (factor: number, viewW: number, viewH: number) =>
      setT((p) => {
        const ns = clamp(p.s * factor, min, max);
        const k = ns / p.s;
        const mx = viewW / 2;
        const my = viewH / 2;
        return { s: ns, ...clampPan(ns, mx - (mx - p.x) * k, my - (my - p.y) * k) };
      }),
    [min, max, clampPan],
  );

  const fit = useCallback(
    (viewW: number, viewH: number, boundsW: number, boundsH: number, pad = 60) => {
      const s = clamp(Math.min((viewW - pad * 2) / boundsW, (viewH - pad * 2) / boundsH), min, max);
      setT({ s, x: (viewW - boundsW * s) / 2, y: Math.max(pad, (viewH - boundsH * s) / 2) });
    },
    [min, max],
  );

  return { t, setT, setBounds, onMouseDown, onMouseMove, endDrag, onWheel, zoomBy, fit };
};
